import React, { useMemo } from 'react';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import {
  clusteringTestDataOptions,
  useClustersForQuestion,
} from '@mentimeter/core-hooks';
import type { ResponseCluster } from '@mentimeter/http-clients';
import { getGroupsShapes } from '@mentimeter/slides-presentation-ui/groups/group-shapes';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useTranslations } from 'next-intl';
import { UNGROUPED_CLUSTER_ID } from '../../../groups';
import { testDataClustering } from '../utils/testData';
import { createUngroupedGroup } from '../../utils/createUngroupedGroup';
import { useStickyResult } from '../../utils/useStickyResult';
import { GroupsWithVotes } from './GroupsWithVotes';

export function GroupsWithVotesDataHandler() {
  const { useQuestion, useTestVotes } = usePresentationContext();
  const translate = useTranslations('presenting.legacy');
  const {
    theme: {
      visualizationColors: { fillColors },
    },
  } = useRagnar();
  const { slideAdminKey: admin_key } = useQuestion();

  const { testVotesShowing } = useTestVotes();
  const { clusteringForQuestion } = useClustersForQuestion({
    shouldFetch: !testVotesShowing,
    questionAdminKey: admin_key,
    testVotesShowing,
    options: testVotesShowing
      ? { ...clusteringTestDataOptions, fallbackData: testDataClustering }
      : {},
  });

  // Using sticky result to prevent the responses from animating out while refetching
  const clusters = useStickyResult(clusteringForQuestion?.clusters);
  const groups = useMemo(() => {
    return clusters ?? [];
  }, [clusters]);
  const currentClusteringId = clusteringForQuestion?.id;
  const ungroupedResponses = clusteringForQuestion?.unclustered_responses ?? [];

  const ungroupedGroup = createUngroupedGroup({
    clusteringId: groups[0]?.clustering_id,
    ungroupedResponses,
    label: translate('open.new_responses'),
  });

  const groupsWithUngrouped = ungroupedGroup
    ? [...groups, ungroupedGroup]
    : groups;

  const groupShapeMap = getGroupsShapes(
    groupsWithUngrouped,
    fillColors,
    UNGROUPED_CLUSTER_ID,
  );
  const sortedGroups: ResponseCluster[] = groupsWithUngrouped.sort(
    (a, b) => b.total_upvotes - a.total_upvotes,
  );

  return (
    <GroupsWithVotes
      groups={sortedGroups}
      clusteringId={currentClusteringId}
      groupShapeMap={groupShapeMap}
    />
  );
}
